import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { message } from 'antd-v5';

import { Button, DatePicker, Form, InputNumber, Modal, Select, Table } from '@/components';
import {
  batchSaleOut,
  getBatchSupplierList,
  getStallBatchList,
} from '@/state/request/batchManager';

import './index.less';

const PAGE_SIZE = 100;
const SALE_TYPE_OPTIONS = [
  { label: '自营', value: 4 },
  { label: '代卖', value: 1 },
];

const getPageData = (response) => response?.data || {};

function BatchList({ stallId }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saleOutLoading, setSaleOutLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: PAGE_SIZE,
    total: 0,
    showTotal: (total) => `共 ${total} 条`,
  });

  const loadSuppliers = useCallback(async () => {
    const response = await getBatchSupplierList({ stallId });
    if (response?.code === 200) {
      setSupplierOptions(
        (response.data || []).map((supplier) => ({
          label: supplier.supplierName,
          value: supplier.supplierPersonId,
        })),
      );
    }
  }, [stallId]);

  const loadList = useCallback(
    async (pageNo = 1) => {
      const values = form.getFieldsValue();
      const [startTime, endTime] = values.createTime || [];
      const params = {
        stallId,
        supplierPersonId: values.supplierPersonId,
        batchSaleType: values.batchSaleType,
        carNo: values.carNo,
        startCreateTime: startTime ? startTime.startOf('day').valueOf() : undefined,
        endCreateTime: endTime ? endTime.add(1, 'day').startOf('day').valueOf() : undefined,
        pageNo,
        pageSize: PAGE_SIZE,
      };

      setLoading(true);
      try {
        const response = await getStallBatchList(params);
        if (response?.code === 200) {
          const page = getPageData(response);
          const pageList = Array.isArray(page) ? page : page.pageList || [];
          setDataSource(pageList);
          setSelectedRowKeys([]);
          setPagination((current) => ({
            ...current,
            current: page.pageNo || pageNo,
            total: Array.isArray(page) ? page.length : page.totalSize || 0,
          }));
        }
      } finally {
        setLoading(false);
      }
    },
    [form, stallId],
  );

  useEffect(() => {
    loadSuppliers();
    loadList();
  }, [loadList, loadSuppliers]);

  const handleReset = () => {
    form.resetFields();
    loadList(1);
  };

  const handleSaleOut = () => {
    Modal.confirm({
      title: '提示',
      content: `确认售罄这 ${selectedRowKeys.length} 个批次吗？`,
      okText: '确认售罄',
      cancelText: '取消',
      okButtonProps: { danger: true },
      async onOk() {
        setSaleOutLoading(true);
        try {
          const response = await batchSaleOut({ stallId, batchIdList: selectedRowKeys });
          if (response?.code === 200) {
            const result = response.data || {};
            const { totalCount = 0, successCount = 0, failCount = 0, failList = [] } = result;

            if (failCount > 0) {
              Modal.warning({
                title: successCount > 0 ? '批次售罄部分成功' : '批次售罄失败',
                width: 620,
                content: (
                  <div className='batch-sale-out-result'>
                    <div className='batch-sale-out-summary'>
                      共处理 {totalCount} 个批次，成功 {successCount} 个，失败 {failCount} 个
                    </div>
                    <Table
                      rowKey={(record, index) => `${record.batchId}-${index}`}
                      size='small'
                      pagination={false}
                      dataSource={failList}
                      columns={[
                        { title: '批次ID', dataIndex: 'batchId', width: 140 },
                        { title: '失败原因', dataIndex: 'reason' },
                      ]}
                      scroll={{ y: 320 }}
                    />
                  </div>
                ),
              });
            } else {
              message.success(`批次售罄成功，共处理 ${successCount} 个批次`);
            }

            if (successCount > 0) {
              await loadList(pagination.current);
            }
          }
        } finally {
          setSaleOutLoading(false);
        }
      },
    });
  };

  const columns = [
    {
      title: '售卖方式',
      dataIndex: 'batchSaleType',
      width: 140,
      render: (value) => (value === 4 ? '自营' : value === 1 ? '代卖' : '--'),
    },
    { title: '货主', dataIndex: 'supplierName' },
    {
      title: '批次号',
      dataIndex: 'carNo',
      width: 160,
      render: (value) =>
        value === null || value === undefined ? '--' : String(value).padStart(4, '0'),
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      width: 220,
      render: (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '--'),
    },
  ];

  return (
    <div className='stall-batch-list'>
      <Form form={form} layout='inline' onFinish={() => loadList(1)}>
        <Form.Item name='batchSaleType'>
          <Select placeholder='售卖方式' options={SALE_TYPE_OPTIONS} />
        </Form.Item>
        <Form.Item name='supplierPersonId'>
          <Select
            placeholder='货主'
            options={supplierOptions}
            showSearch
            optionFilterProp='label'
          />
        </Form.Item>
        <Form.Item name='carNo'>
          <InputNumber
            placeholder='批次'
            min={1}
            precision={0}
            controls={false}
            parser={(value) => (value ? value.replace(/\D/g, '') : '')}
            onKeyDown={(event) => {
              if (['.', '+', '-', 'e', 'E'].includes(event.key)) {
                event.preventDefault();
              }
            }}
          />
        </Form.Item>
        <Form.Item name='createTime'>
          <DatePicker.RangePicker
            placeholder={['创建开始时间', '创建结束时间']}
            disabledDate={(current) => current && current > dayjs().endOf('day')}
          />
        </Form.Item>
        <Form.Item>
          <Button type='primary' htmlType='submit'>
            查询
          </Button>
          <Button onClick={handleReset}>重置</Button>
          <Button
            danger
            disabled={!selectedRowKeys.length}
            loading={saleOutLoading}
            onClick={handleSaleOut}
          >
            批量售罄
          </Button>
        </Form.Item>
      </Form>

      <Table
        rowKey='batchId'
        loading={loading}
        columns={columns}
        dataSource={dataSource}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
        pagination={pagination}
        onChange={(page) => loadList(page.current)}
      />
    </div>
  );
}

export default BatchList;
